import React, { useState, useEffect, useRef, useMemo } from 'react';
import * as d3 from 'd3';
import AdminPage from './AdminPage';
import { trackPageview, trackEvent } from './analytics';

// Transliteration map: Latin to Cyrillic (covers both Russian and Ukrainian)
function transliterateToRussian(text) {
  const map = {
    'shch': 'щ', 'sch': 'щ', 'kh': 'х', 'zh': 'ж', 'ts': 'ц',
    'ch': 'ч', 'sh': 'ш', 'yu': 'ю', 'ya': 'я', 'yo': 'ё',
    'yi': 'ї', 'ye': 'є', 'iy': 'ій',
    'a': 'а', 'b': 'б', 'v': 'в', 'g': 'г', 'd': 'д', 'e': 'е',
    'z': 'з', 'i': 'и', 'y': 'й', 'k': 'к', 'l': 'л', 'm': 'м',
    'n': 'н', 'o': 'о', 'p': 'п', 'r': 'р', 's': 'с', 't': 'т',
    'u': 'у', 'f': 'ф', 'h': 'х', 'w': 'в', 'x': 'кс',
    'j': 'й', 'c': 'к', 'q': 'к'
  };
  let result = text.toLowerCase();
  const sortedKeys = Object.keys(map).sort((a, b) => b.length - a.length);
  for (const key of sortedKeys) {
    result = result.replace(new RegExp(key, 'g'), map[key]);
  }
  return result;
}

// Translations object — defined once outside component to avoid recreation on every render
const TRANSLATIONS = {
    uk: {
      appTitle: "Украдені скарби",
      subtitle: "Українські артефакти у російських музеях",
      searchPlaceholder: "Пошук артефактів (Київ, монета, амфора, Бісер...)...",
      selectZone: "🖼️ Вибрати зону",
      cancel: "❌ Скасувати",
      resetZoom: "🔄 Скинути zoom",
      period: "Період",
      noDate: "Без дати",
      total: "Усього",
      shm: "ДІМ",
      hermitage: "Ермітаж",
      homePage: "Домашня сторінка",
      shmFull: "Державний історичний музей Росії",
      hermitageFull: "Ермітаж",
      welcome: "Ласкаво просимо до колекції \"Украдені скарби\".",
      welcomeText: "Ця колекція створена з ресурсів, зібраних з російських музеїв, які привласнили артефакти, знайдені на території України. Назви та місця знахідок перекладені українською мовою за допомогою штучного інтелекту — переклад може містити неточності. Оригінальні метадані були опубліковані музеями російською — ми зберігаємо їх як документальне свідчення.",
      collectionStored: "Колекція зберігається в каталозі з інтерактивним просторово-часовим інтерфейсом, який дозволє користувачам переглядати колекцію за місця та різні періоди часу та в різних імперії",
      markerHelp: "Натисніть маркери масштабування часу, щоб вибрати періоди часу, які вас цікавлять. Натисніть будь-яке коло на карті, щоб отримати ресурси про це локацію. Коли різниться за розміром і пов'язними з ними документами залежить від обраної часо",
      copyrightNotice: "Застереження щодо авторських прав:",
      copyrightText: "Авторські права на зображення артефактів і метадані, розміщені на цьому сайті, не заявляються. У випадках, коли використання матеріалів може виглядати як потенційне порушення, ми вважаємо, що інтерактивна просторово-часова візуалізація колекцій є допустимим використанням згідно з принципами добросовісного використання, передбаченими законами про авторське право США та Канади. Якщо ви вважаєте, що матеріал було використано без належного дозволу, будь ласка, зв'яжіться з автором для вирішення цього питання.",
      collageNotice: "Примітка щодо колажу:",
      collageText: "Колаж зображень показує артефакти з обраної локації.",
      selectOnMap: "Натисніть на коло на карті, щоб переглянути артефакти з цієї локації.",
      clickCircle: "Натисніть будь-яке коло на карті для інформації.",
      resultCount: "Кількість результатів:",
      viewCollage: "Переглянути колаж",
      page: "Сторінка",
      resources: "Ресурси",
      artifacts: "артефактів",
      image: "Зображення",
      title: "Назва",
      date: "Дата",
      location: "Місце",
      closeCollage: "Закрити колаж",
      noTitle: "Без назви",
      dateUnknown: "Дата невідома",
      bce: "до н.е.",
      ce: "н.е.",
      historicalTime: "Історичний час",
      timelineInstruction: "Історичний час - Виберіть період на нижній панелі",
      dragToSelect: "Перетягніть прямокутник на карті щоб вибрати артефакти",
      periods: {
        paleolithic: "Палеоліт",
        mesolithic: "Мезоліт",
        neolithic: "Неоліт",
        scythian: "Скіфська доба",
        sarmatian: "Сарматська доба",
        antiquity: "Античність",
        migration: "Епоха Великого переселення народів",
        kyivanRus: "Епоха Русі",
        galiciaVolhynia: "Епоха Галицько-Волинської держави",
        lithuanianPolish: "Литовсько-Польська доба",
        cossack: "Козацька доба",
        imperial: "Імперська доба",
        twentieth: "XX століття",
        contemporary: "Сучасність"
      },
      // About section
      aboutProject: "Про проєкт",
      aboutText: "«Украдені скарби» — це інтерактивна карта понад 110 000 українських артефактів, які наразі зберігаються у двох найбільших російських музеях: Державному історичному музеї та Ермітажі. Ці предмети були знайдені на території України, але протягом століть — від часів Російської імперії до радянської доби й аж до сьогодні — систематично вивозилися та представлялися як частина «російської» спадщини.",
      aboutViz: "Ця візуалізація дає змогу побачити масштаб привласнення і водночас відкрити для себе багатошарову історію, закарбовану в кожному артефакті.",
      howToUse: "Як працювати з картою",
      howCircles: "Кола на карті показують місця, де були знайдені артефакти. Розмір кола відповідає кількості предметів з цієї локації. Натисніть на будь-яке коло, щоб побачити перелік артефактів із назвами, датами та місцем знахідки.",
      howTimeline: "Часова шкала внизу екрана — це стрімграф, який показує розподіл артефактів за історичними періодами: від палеоліту до сучасності. Натисніть на будь-який період, щоб відфільтрувати карту і побачити лише артефакти з цього часу.",
      howSearch: "Пошук дозволяє знайти конкретні артефакти за ключовими словами — наприклад, «монета», «амфора», «Київ» або «гребінь». Назви перекладені українською, тому пошук працює як кирилицею, так і латиницею — система автоматично транслітерує латинські літери. Наприклад, ви можете набрати «moneta» або «Kyiv», і пошук знайде відповідні артефакти.",
      howZone: "Вибір зони — перетягніть прямокутник на карті, щоб виділити географічну область і переглянути всі артефакти з неї.",
      howCollage: "Колаж — кнопка «Переглянути колаж» відкриває мозаїку зображень артефактів з обраної колекції, даючи змогу візуально оцінити їх різноманітність.",
      howTabs: "Вкладки колекцій — перемикайтеся між колекціями Державного історичного музею та Ермітажу, щоб порівняти, які типи артефактів зберігає кожен з музеїв.",
      discoverTitle: "Як відкривати історії",
      discoverIntro: "Карта — це не просто каталог. Це інструмент для відкриттів.",
      discoverRegion: "Дослідіть свій регіон.",
      discoverRegionText: "Знайдіть на карті свою область чи місто. Що було знайдено саме там? Які епохи представлені? Можливо, ви дізнаєтесь, що у вашому краї існували торгові зв'язки з Римською імперією, або що поблизу вашого міста знаходили скіфські скарби.",
      discoverTime: "Подорожуйте в часі.",
      discoverTimeText: "Оберіть історичний період на часовій шкалі та спостерігайте, як змінюється географія знахідок. Скіфська доба зосереджена на півдні та сході, козацька — вздовж Дніпра, а артефакти Київської Русі — навколо стародавніх міст.",
      discoverPatterns: "Шукайте закономірності.",
      discoverPatternsText: "Коли ви бачите скупчення кіл в одному місці, це може свідчити про давній торговий шлях, культовий центр або стратегічне поселення. Наприклад, концентрація римських монет на Рівненщині відкриває нові питання про торгові контакти між Римом і племенами Волині.",
      discoverMuseums: "Порівнюйте музеї.",
      discoverMuseumsText: "Перемикаючись між колекціями, зверніть увагу: які регіони України «представлені» в якому музеї? Це може розповісти про різні хвилі привласнення — імперську, радянську та сучасну.",
      discoverConnections: "Відкривайте зв'язки.",
      discoverConnectionsText: "Кожен артефакт — це свідчення. Половецькі кам'яні статуї з Бахмутщини говорять про кочові цивілізації XI–XIII століть. Гемми з Ольвії — про зв'язки з Римом. Сережки з Києва та Чернігова — про ювелірну майстерність Русі. Разом вони складають історію, яку Росія століттями переписувала під свій наратив.",
      whyMattersTitle: "Чому це важливо",
      whyMattersText: "У нас ніколи не було єдиної зібраної історії. Наші артефакти розкидані по музеях світу — не лише в Росії, а й у Польщі, Німеччині, Канаді, США та інших країнах. Ця платформа — крок до того, щоб зв'язати всі ці свідчення докупи та краще зрозуміти, хто ми є."
    },
    en: {
      appTitle: "Stolen Treasures",
      subtitle: "Ukrainian Artifacts in Russian Museums",
      searchPlaceholder: "Search artifacts (moneta, amfora...)...",
      selectZone: "🖼️ Select Zone",
      cancel: "❌ Cancel",
      resetZoom: "🔄 Reset Zoom",
      period: "Period",
      noDate: "No Date",
      total: "Total",
      shm: "SHM",
      hermitage: "Hermitage",
      homePage: "Home Page",
      shmFull: "State Historical Museum of Russia",
      hermitageFull: "Hermitage",
      welcome: "Welcome to the \"Stolen Treasures\" collection.",
      welcomeText: "This collection is created from resources gathered from Russian museums that have appropriated artifacts found on the territory of Ukraine. Names and find locations have been translated into Ukrainian using AI — translations may occasionally be inaccurate. The original metadata was published by the museums in Russian — we preserve it as documentary evidence.",
      collectionStored: "The collection is stored in a catalog with an interactive spatio-temporal interface that allows users to browse the collection by locations and different time periods and different empires.",
      markerHelp: "Click the time zoom markers to select time periods of interest to you. Click any circle on the map to get resources about that location. When varying in size and associated documents depends on the selected time scale.",
      copyrightNotice: "Copyright Notice:",
      copyrightText: "No copyright claims are made on the images of artifacts and metadata posted on this site. In cases where the use of materials may appear to be a potential infringement, we believe that the interactive spatio-temporal visualization of collections is a permitted use under the principles of fair use provided by U.S. and Canadian copyright laws. If you believe that material has been used without proper permission, please contact the author to resolve this issue.",
      collageNotice: "Collage Note:",
      collageText: "The image collage shows artifacts from the selected location.",
      selectOnMap: "Click on a circle on the map to view artifacts from that location.",
      clickCircle: "Click any circle on the map for information.",
      resultCount: "Results:",
      viewCollage: "View Collage",
      page: "Page",
      resources: "Resources",
      artifacts: "artifacts",
      image: "Image",
      title: "Title",
      date: "Date",
      location: "Location",
      closeCollage: "Close Collage",
      noTitle: "No title",
      dateUnknown: "Date unknown",
      bce: "BCE",
      ce: "CE",
      historicalTime: "Historical Timeline",
      timelineInstruction: "Historical Timeline - Select period on the bottom panel",
      dragToSelect: "Drag a rectangle on the map to select artifacts",
      periods: {
        paleolithic: "Paleolithic",
        mesolithic: "Mesolithic",
        neolithic: "Neolithic",
        scythian: "Scythian Period",
        sarmatian: "Sarmatian Period",
        antiquity: "Antiquity",
        migration: "Migration Period",
        kyivanRus: "Kyivan Rus Period",
        galiciaVolhynia: "Galicia-Volhynia Period",
        lithuanianPolish: "Lithuanian-Polish Period",
        cossack: "Cossack Period",
        imperial: "Imperial Period",
        twentieth: "20th Century",
        contemporary: "Contemporary"
      },
      // About section
      aboutProject: "About the Project",
      aboutText: "\"Stolen Treasures\" is an interactive map of over 110,000 Ukrainian artifacts currently held in two of Russia's largest museums: the State Historical Museum and the Hermitage. These objects were found on Ukrainian territory but were systematically removed over centuries — from the Russian Empire through the Soviet era to the present — and presented as part of \"Russian\" heritage.",
      aboutViz: "This visualization reveals the scale of this appropriation while enabling users to discover the layered history embedded in each artifact.",
      howToUse: "How to Use the Map",
      howCircles: "Circles on the map mark locations where artifacts were found. Circle size corresponds to the number of items from that location. Click any circle to view a list of artifacts with titles, dates, and find spots.",
      howTimeline: "The timeline at the bottom of the screen is a streamgraph showing the distribution of artifacts across historical periods — from the Paleolithic to the present. Click any period to filter the map and see only artifacts from that era.",
      howSearch: "Search lets you find specific artifacts by keyword — for example, \"moneta,\" \"amfora,\" or \"biser.\" Names and locations have been translated into Ukrainian, so the search works with both Cyrillic and Latin characters — the system automatically transliterates Latin input. For example, you can type \"moneta\" or \"Kyiv\" and the search will find matching artifacts.",
      howZone: "Zone selection — drag a rectangle on the map to highlight a geographic area and view all artifacts within it.",
      howCollage: "Collage — the \"View Collage\" button opens a mosaic of artifact images from the selected collection, giving you a visual sense of their diversity.",
      howTabs: "Collection tabs — switch between the State Historical Museum and Hermitage collections to compare what types of artifacts each museum holds.",
      discoverTitle: "How to Discover Stories",
      discoverIntro: "The map is not just a catalog. It is a tool for discovery.",
      discoverRegion: "Explore your region.",
      discoverRegionText: "Find your oblast or city on the map. What was found there? Which eras are represented? You may discover that your region had trade links with the Roman Empire, or that Scythian treasures were found near your town.",
      discoverTime: "Travel through time.",
      discoverTimeText: "Select a historical period on the timeline and watch how the geography of finds shifts. The Scythian era is concentrated in the south and east, the Cossack period along the Dnipro, and Kyivan Rus artifacts cluster around ancient cities.",
      discoverPatterns: "Look for patterns.",
      discoverPatternsText: "When you see clusters of circles in one area, it may indicate an ancient trade route, a sacred site, or a strategic settlement. For example, a concentration of Roman coins in the Rivne region raises new questions about trade contacts between Rome and the tribes of Volhynia.",
      discoverMuseums: "Compare museums.",
      discoverMuseumsText: "As you switch between collections, notice which regions of Ukraine are \"represented\" in which museum. This can reveal different waves of appropriation — imperial, Soviet, and contemporary.",
      discoverConnections: "Discover connections.",
      discoverConnectionsText: "Every artifact is a piece of evidence. Polovtsian stone statues from the Bakhmut region speak to the nomadic civilizations of the 11th–13th centuries. Gemstones from Olbia reveal connections with Rome. Earrings from Kyiv and Chernihiv showcase the jewelry craftsmanship of Kyivan Rus. Together, they form a history that Russia has rewritten under its own narrative for centuries.",
      whyMattersTitle: "Why This Matters",
      whyMattersText: "We have never had a unified, collected history. Our artifacts are scattered across museums worldwide — not only in Russia, but also in Poland, Germany, Canada, the United States, and beyond. This platform is a step toward linking all of this evidence together and better understanding who we are."
    }
  };

function App() {
  const [hash, setHash] = useState(window.location.hash);
  // Language state
  const [language, setLanguage] = useState('uk'); // 'uk' or 'en'

  const translations = TRANSLATIONS;
  const t = translations[language]; // Current translations

  // Historical periods — depend on t (language), memoized to avoid recreation
  const historicalPeriods = useMemo(() => [
    { name: t.periods.paleolithic, start: -135000, end: -10000, color: "#fef0d9" },
    { name: t.periods.mesolithic, start: -10000, end: -7000, color: "#fdd49e" },
    { name: t.periods.neolithic, start: -7000, end: -4000, color: "#fdbb84" },
    { name: t.periods.scythian, start: -4000, end: -3000, color: "#fc8d59" },
    { name: t.periods.sarmatian, start: -3000, end: -200, color: "#ef6548" },
    { name: t.periods.antiquity, start: -800, end: 400, color: "#d7301f" },
    { name: t.periods.migration, start: 300, end: 700, color: "#b30000" },
    { name: t.periods.kyivanRus, start: 862, end: 1240, color: "#7f0000" },
    { name: t.periods.galiciaVolhynia, start: 1199, end: 1349, color: "#8c510a" },
    { name: t.periods.lithuanianPolish, start: 1349, end: 1648, color: "#d8b365" },
    { name: t.periods.cossack, start: 1500, end: 1800, color: "#f6e8c3" },
    { name: t.periods.imperial, start: 1800, end: 1917, color: "#c7eae5" },
    { name: t.periods.twentieth, start: 1900, end: 2000, color: "#5ab4ac" },
    { name: t.periods.contemporary, start: 2000, end: 2022, color: "#01665e" }
  ], [t]);

  // Helper function to format dates with BCE/CE — memoized since it depends on language
  const formatDate = React.useCallback((date) => {
    if (!date || date === 'N/A' || date === t.dateUnknown) {
      return date;
    }
    const numDate = Number(date);
    if (isNaN(numDate)) return date;
    if (numDate < 0) return `${Math.abs(numDate)} ${t.bce}`;
    if (numDate === 0) return '0';
    return `${numDate} ${t.ce}`;
  }, [t]);

  const [selectedTab, setSelectedTab] = useState(0);
  const [timeRange, setTimeRange] = useState([0, 2022]);
  const [allData, setAllData] = useState({ 
    shm: [], 
    herm: [], 
    stream: [], 
    coords: {}, 
    geojson: null,
    database: [],
    labels: {}
  });
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedArtifacts, setSelectedArtifacts] = useState([]);
  const [selectedArtifact, setSelectedArtifact] = useState(null); // single artifact detail panel
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  
  const mapRef = useRef(null);
  const streamgraphRef = useRef(null);
  const zoomRef = useRef(null);
  const isInitializingBrush = useRef(false);

  // Load all data from combined_database.csv
  useEffect(() => {
    const loadData = async () => {
      try {
        
        const [streamData, coordsData, geojsonData, databaseData, labelsData] = await Promise.all([
          d3.csv('./stream.csv'),
          d3.json('./coordinates.json'),
          d3.json('./geoBoundaries-UKR-ADM0_simplified.geojson'),
          d3.csv('./combined_database.csv'),
          d3.json('./labels.json')
        ]);


        const processedDatabase = databaseData.map(d => ({
          ...d,
          id: d.id,
          dates: +d.dates,
          source: d.source,
          coords: d.coords,
          img: d.img1?.startsWith('/') ? `https://dataforlibs.github.io${d.img1}` : d.img1,
          img1: d.img1?.startsWith('/') ? `https://dataforlibs.github.io${d.img1}` : d.img1,
          title: d.title,
          Название: d.Название,
          'Место находки': d['Место находки'],
          'Ключевые слова': d['Ключевые слова'],
          // Ukrainian translations (from batch translation)
          Назва_uk: d.Назва_uk || '',
          'Місце_знахідки_uk': d['Місце_знахідки_uk'] || '',
          'Місце_створення_uk': d['Місце_створення_uk'] || '',
          searchText: `${d.title || ''} ${d.Название || ''} ${d.Назва_uk || ''} ${d['Место находки'] || ''} ${d['Місце_знахідки_uk'] || ''} ${d['Ключевые слова'] || ''}`.toLowerCase()
        }));

        // Filter by source field
        const shmData = processedDatabase.filter(d => d.source === 'SHM');
        const hermData = processedDatabase.filter(d => d.source === 'Hermitage');


        setAllData({
          shm: shmData,
          herm: hermData,
          stream: streamData.map(d => ({ 
            dates: +d.dates, 
            SHM: +d.SHM, 
            Hermitage: +d.Hermitage 
          })),
          coords: coordsData,
          geojson: geojsonData,
          database: processedDatabase,
          labels: labelsData
        });
        
        setLoading(false);
        trackPageview();
      } catch (error) {
        setLoading(false);
        
      }
    };
    loadData();
  }, []);

  // Filter data based on time range and search using useMemo to prevent infinite loops
  const filteredData = useMemo(() => {
    
    if (allData.shm.length === 0) {
      return { shm: [], herm: [] };
    }

    let shmFiltered = allData.shm.filter(d => d.dates >= timeRange[0] && d.dates <= timeRange[1]);
    let hermFiltered = allData.herm.filter(d => d.dates >= timeRange[0] && d.dates <= timeRange[1]);


    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const transliterated = transliterateToRussian(query);
      
      const searchIds = new Set(
        allData.database
          .filter(d => d.searchText.includes(query) || d.searchText.includes(transliterated))
          .map(d => d.id?.toString())
      );
      
      shmFiltered = shmFiltered.filter(d => searchIds.has(d.id?.toString()));
      hermFiltered = hermFiltered.filter(d => searchIds.has(d.id?.toString()));
      
    }

    const result = {
      shm: shmFiltered,
      herm: hermFiltered
    };
    
    return result;
  }, [timeRange, allData, searchQuery]);

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    trackEvent('search_query', { query });
    if (query.trim() && allData.database.length > 0) {
      const lowerQuery = query.toLowerCase();
      const transliterated = transliterateToRussian(lowerQuery);
      
      const results = allData.database
        .filter(d => d.searchText.includes(lowerQuery) || d.searchText.includes(transliterated))
        .slice(0, 10);
      setSearchResults(results);
      setShowDropdown(results.length > 0);
    } else {
      setSearchResults([]);
      setShowDropdown(false);
    }
  };

  useEffect(() => {
    if (!allData.geojson || !mapRef.current) return;

    const isMobile = window.innerWidth <= 768;
    const containerHeight = isMobile ? 250 : 670;

    d3.select(mapRef.current).selectAll('*').remove();

    const svg = d3.select(mapRef.current).append('svg');

    svg
      .attr('width', svg.node().parentNode.clientWidth)
      .attr('height', containerHeight);
    
    const actualWidth = svg.node().parentNode.clientWidth;
    const g = svg.append('g');

    const projection = d3.geoMercator()
      .center([31.16, 48.38])
      .scale(2200)
      .translate([actualWidth / 2, containerHeight / 2]);

    const path = d3.geoPath().projection(projection);

    g.append('path')
      .datum(allData.geojson)
      .attr('d', path)
      .attr('fill', '#f8f9fa')
      .attr('stroke', '#333')
      .attr('stroke-width', 2);

    const groupData = (data, coordsObj) => {
      const grouped = {};
      data.forEach(d => {
        const coordKey = d.coords;
        if (!grouped[coordKey]) {
          grouped[coordKey] = { coords: coordKey, count: 0, artifacts: [] };
        }
        grouped[coordKey].count++;
        grouped[coordKey].artifacts.push(d);
      });
      return Object.values(grouped);
    };

    const groupedShm = groupData(filteredData.shm, allData.coords.coords_shm);
    const groupedHerm = groupData(filteredData.herm, allData.coords.coords_herm);

    const maxCount = Math.max(
      d3.max(groupedShm, d => d.count) || 0,
      d3.max(groupedHerm, d => d.count) || 0
    );
    
    const radiusScale = d3.scaleSqrt()
      .domain([0, maxCount])
      .range([3, 25]);

    const shmCircles = g.append('g').attr('class', 'shm-circles');
    const circles = shmCircles.selectAll('.circle-shm')
      .data(groupedShm)
      .enter()
      .append('circle')
      .attr('class', 'circle-shm')
      .attr('cx', d => {
        const coords = allData.coords.coords_shm[d.coords];
        if (!coords || isNaN(+coords[0]) || isNaN(+coords[1])) return -100;
        return projection([+coords[1], +coords[0]])[0];
      })
      .attr('cy', d => {
        const coords = allData.coords.coords_shm[d.coords];
        if (!coords || isNaN(+coords[0]) || isNaN(+coords[1])) return -100;
        return projection([+coords[1], +coords[0]])[1];
      })
      .attr('r', d => radiusScale(d.count))
      .attr('fill', 'rgba(84, 112, 198, 0.6)')  // Blue to match timeline
      .attr('stroke', '#5470C6')
      .attr('stroke-width', 1.5)
      .style('cursor', 'pointer')
      .on('click', function(event, d) {
        setSelectedTab(1);
        setSelectedLocation({ type: 'SHM', data: d });
        setCurrentPage(1);
        trackEvent('map_click', { type: 'SHM', location: d.coords, count: d.count });
      })
      .on('mouseenter', function() {
        d3.select(this).attr('fill', 'rgba(84, 112, 198, 0.9)');
      })
      .on('mouseleave', function() {
        d3.select(this).attr('fill', 'rgba(84, 112, 198, 0.6)');
      });
    
    // Add tooltips
    circles.append('title')
      .text(d => {
        const locationName = allData.labels?.labels_shm?.[d.coords]?.[0] || '';
        const baseText = `${t.shmFull}: ${d.count} ${t.artifacts}`;
        return locationName ? `${baseText}\n${locationName}` : baseText;
      });

    const hermCircles = g.append('g').attr('class', 'herm-circles');
    const hermCirclesSelection = hermCircles.selectAll('.circle-herm')
      .data(groupedHerm)
      .enter()
      .append('circle')
      .attr('class', 'circle-herm')
      .attr('cx', d => {
        const coords = allData.coords.coords_herm[d.coords];
        if (!coords || isNaN(+coords[0]) || isNaN(+coords[1])) return -100;
        return projection([+coords[1], +coords[0]])[0];
      })
      .attr('cy', d => {
        const coords = allData.coords.coords_herm[d.coords];
        if (!coords || isNaN(+coords[0]) || isNaN(+coords[1])) return -100;
        return projection([+coords[1], +coords[0]])[1];
      })
      .attr('r', d => radiusScale(d.count))
      .attr('fill', 'rgba(145, 204, 117, 0.6)')  // Green to match timeline
      .attr('stroke', '#91CC75')
      .attr('stroke-width', 1.5)
      .style('cursor', 'pointer')
      .on('click', function(event, d) {
        setSelectedTab(2);
        setSelectedLocation({ type: 'Hermitage', data: d });
        setCurrentPage(1);
        trackEvent('map_click', { type: 'Hermitage', location: d.coords, count: d.count });
      })
      .on('mouseenter', function() {
        d3.select(this).attr('fill', 'rgba(145, 204, 117, 0.9)');
      })
      .on('mouseleave', function() {
        d3.select(this).attr('fill', 'rgba(145, 204, 117, 0.6)');
      });
    
    // Add tooltips
    hermCirclesSelection.append('title')
      .text(d => {
        const locationName = allData.labels?.labels_herm?.[d.coords]?.[0] || '';
        const baseText = `${t.hermitageFull}: ${d.count} ${t.artifacts}`;
        return locationName ? `${baseText}\n${locationName}` : baseText;
      });

    const zoom = d3.zoom()
      .scaleExtent([1, 8])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
      });

    svg.call(zoom);
    zoomRef.current = zoom;

    if (selectionMode) {
      const brush = d3.brush()
        .extent([[0, 0], [actualWidth, containerHeight]])
        .on('end', (event) => {
          if (!event.selection) return;
          
          const [[x0, y0], [x1, y1]] = event.selection;
          const selected = [];
          
          groupedShm.forEach(group => {
            const coords = allData.coords.coords_shm[group.coords];
            if (coords && !isNaN(+coords[0]) && !isNaN(+coords[1])) {
              const [px, py] = projection([+coords[1], +coords[0]]);
              if (px >= x0 && px <= x1 && py >= y0 && py <= y1) {
                selected.push(...group.artifacts);
              }
            }
          });
          
          groupedHerm.forEach(group => {
            const coords = allData.coords.coords_herm[group.coords];
            if (coords && !isNaN(+coords[0]) && !isNaN(+coords[1])) {
              const [px, py] = projection([+coords[1], +coords[0]]);
              if (px >= x0 && px <= x1 && py >= y0 && py <= y1) {
                selected.push(...group.artifacts);
              }
            }
          });
          
          setSelectedArtifacts(selected);
          setSelectionMode(false);
        });

      svg.append('g')
        .attr('class', 'brush')
        .call(brush);
    }

  }, [allData, filteredData, selectionMode, language]);

  useEffect(() => {
    const onHash = () => {
      const h = window.location.hash;
      setHash(h);
      // Handle direct artifact links: #artifact-{id}
      if (h.startsWith('#artifact-') && allData.database.length > 0) {
        const id = h.replace('#artifact-', '');
        const found = allData.database.find(d => String(d.id) === String(id));
        if (found) setSelectedArtifact(found);
      } else if (!h.startsWith('#artifact-')) {
        setSelectedArtifact(null);
      }
    };
    window.addEventListener('hashchange', onHash);
    // Handle hash on initial load
    onHash();
    return () => window.removeEventListener('hashchange', onHash);
  }, [allData.database]);




  // Streamgraph with period labels
  useEffect(() => {
    if (allData.stream.length === 0 || !streamgraphRef.current) return;

    const containerWidth = streamgraphRef.current.clientWidth || 1000;
    const isMobile = window.innerWidth <= 768;
    const margin = { top: 50, right: 50, bottom: 80, left: isMobile ? 40 : 60 };
    const width = containerWidth - margin.left - margin.right;
    const height = 250 - margin.top - margin.bottom;
    const contextHeight = 100;
    const contextMarginTop = 20;

    d3.select(streamgraphRef.current).selectAll('*').remove();

    const svg = d3.select(streamgraphRef.current)
      .append('svg')
      .attr('width', containerWidth)
      .attr('height', height + margin.top + margin.bottom + contextHeight + contextMarginTop + 100);

    const g = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    const contextG = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top + height + contextMarginTop})`);

    const keys = ['Hermitage', 'SHM']; // Try reversing the order
    const stack = d3.stack()
      .keys(keys)
      .offset(d3.stackOffsetSilhouette)
      .order(d3.stackOrderInsideOut);

    const series = stack(allData.stream);

    const maxActualCount = d3.max(allData.stream, d => d.SHM + d.Hermitage);

    // Non-linear time transformation: 800 CE to present takes 40% of timeline
    const breakYear = 800;
    const dataExtent = d3.extent(allData.stream, d => d.dates);
    const minDate = dataExtent[0];
    const maxDate = dataExtent[1];
    
    // Calculate transformed range
    const ancientRange = 0.6; // 60% for dates < 800
    const modernRange = 0.4;  // 40% for dates >= 800
    
    const transformDate = (date) => {
      if (date < breakYear) {
        // Map minDate to breakYear onto 0 to 0.6
        return ((date - minDate) / (breakYear - minDate)) * ancientRange;
      } else {
        // Map breakYear to maxDate onto 0.6 to 1.0
        return ancientRange + ((date - breakYear) / (maxDate - breakYear)) * modernRange;
      }
    };
    
    const invertTransformedDate = (transformed) => {
      if (transformed < ancientRange) {
        // Map 0 to 0.6 back to minDate to breakYear
        return minDate + (transformed / ancientRange) * (breakYear - minDate);
      } else {
        // Map 0.6 to 1.0 back to breakYear to maxDate
        return breakYear + ((transformed - ancientRange) / modernRange) * (maxDate - breakYear);
      }
    };

    const xScale = d3.scaleLinear()
      .domain([0, 1]) // Transformed domain
      .range([0, width]);

    // Wrapper function that transforms dates before scaling
    const xScaleDate = (date) => xScale(transformDate(date));
    xScaleDate.domain = () => dataExtent;
    xScaleDate.range = () => [0, width];
    xScaleDate.invert = (x) => invertTransformedDate(xScale.invert(x));
    
    const xScaleContext = d3.scaleLinear()
      .domain([0, 1])
      .range([0, width]);
    
    const xScaleContextDate = (date) => xScaleContext(transformDate(date));
    xScaleContextDate.domain = () => dataExtent;
    xScaleContextDate.range = () => [0, width];
    xScaleContextDate.invert = (x) => invertTransformedDate(xScaleContext.invert(x));

    const yScale = d3.scaleLinear()
      .domain([
        d3.min(series, layer => d3.min(layer, d => d[0])),
        d3.max(series, layer => d3.max(layer, d => d[1]))
      ])
      .range([height, 0]);

    const yScaleAxis = d3.scaleLinear()
      .domain([0, maxActualCount])
      .range([height, 0]);

    const yScaleContext = d3.scaleLinear()
      .domain(yScale.domain())
      .range([contextHeight, 0]);

    const colorScale = d3.scaleOrdinal()
      .domain(keys)
      .range(['#91CC75', '#5470C6']); // Green for Hermitage (first), Blue for SHM (second)

    const area = d3.area()
      .x(d => xScaleDate(d.data.dates))
      .y0(d => yScale(d[0]))
      .y1(d => yScale(d[1]))
      .curve(d3.curveBasis);

    const areaContext = d3.area()
      .x(d => xScaleContextDate(d.data.dates))
      .y0(d => yScaleContext(d[0]))
      .y1(d => yScaleContext(d[1]))
      .curve(d3.curveBasis);

    g.append('defs')
      .append('clipPath')
      .attr('id', 'clip')
      .append('rect')
      .attr('width', width)
      .attr('height', height);

    const mainLayers = g.append('g')
      .attr('clip-path', 'url(#clip)')
      .selectAll('.layer')
      .data(series)
      .enter()
      .append('path')
      .attr('class', 'layer')
      .attr('d', area)
      .attr('fill', d => colorScale(d.key))
      .attr('stroke', 'black')
      .attr('stroke-width', 1)
      .attr('opacity', 0.7);

    // Draw context streamgraph WITHOUT period labels
    contextG.selectAll('.layer-context')
      .data(series)
      .enter()
      .append('path')
      .attr('class', 'layer-context')
      .attr('d', areaContext)
      .attr('fill', d => colorScale(d.key))
      .attr('stroke', 'black')
      .attr('stroke-width', 1)
      .attr('opacity', 0.5);

    // Function to draw period labels in main view
    const drawPeriodLabels = (scale) => {
      g.selectAll('.period-rect').remove();
      g.selectAll('.period-label').remove();

      // Get current domain
      const domain = scale.domain();
      const domainStart = domain[0];
      const domainEnd = domain[1];

      // Create a group for period labels with clipping
      const periodGroup = g.insert('g', ':first-child')
        .attr('clip-path', 'url(#clip)');

      const visiblePeriods = [];
      
      historicalPeriods.forEach(period => {
        // Skip periods that don't intersect with current domain
        if (period.end < domainStart || period.start > domainEnd) {
          return;
        }
        
        const x1 = scale(period.start);
        const x2 = scale(period.end);
        const rectWidth = x2 - x1;
        
        // Calculate how much of the period is visible
        const visibleStart = Math.max(0, x1);
        const visibleEnd = Math.min(width, x2);
        const visibleWidth = visibleEnd - visibleStart;
        
        // Only consider if at least 10% of period width is visible
        if (visibleWidth > 0 && rectWidth > 5) {
          visiblePeriods.push({
            ...period,
            x1,
            x2,
            rectWidth,
            visibleWidth,
            centerX: (Math.max(0, x1) + Math.min(width, x2)) / 2
          });
        }
      });

      // Draw all period rectangles (even if no label)
      visiblePeriods.forEach(period => {
        periodGroup.append('rect')
          .attr('class', 'period-rect')
          .attr('x', period.x1)
          .attr('y', 0)
          .attr('width', period.rectWidth)
          .attr('height', height)
          .attr('fill', period.color)
          .attr('opacity', 0.25)
          .attr('stroke', 'black')
          .attr('stroke-width', 0.5);
      });

      // Filter periods for labels - only show if wide enough AND not too crowded
      const labelPeriods = visiblePeriods.filter(p => p.visibleWidth > 60);
      
      // Sort by width descending - show widest periods first
      labelPeriods.sort((a, b) => b.visibleWidth - a.visibleWidth);
      
      // Track used label positions to prevent overlap
      const usedPositions = [];
      const minLabelSpacing = 70;
      
      labelPeriods.forEach(period => {
        // Check if this position would overlap with existing labels
        const wouldOverlap = usedPositions.some(pos => 
          Math.abs(pos - period.centerX) < minLabelSpacing
        );
        
        if (!wouldOverlap) {
          periodGroup.append('text')
            .attr('class', 'period-label')
            .attr('x', period.centerX)
            .attr('y', height / 2-10)
            .attr('text-anchor', 'middle')
            .attr('dominant-baseline', 'middle')
            .style('font-size', '9px')
            .style('font-weight', 'bold')
            .style('fill', '#333')
            .style('pointer-events', 'none')
            .text(period.name);
          
          usedPositions.push(period.centerX);
        }
      });
    };

    // Draw initial period labels
    drawPeriodLabels(xScaleDate);

    const yAxis = d3.axisLeft(yScaleAxis)
      .ticks(5)
      .tickFormat(d => Math.round(d));

    g.append('g')
      .attr('class', 'y-axis')
      .call(yAxis)
      .selectAll('text')
      .style('font-size', '11px');

    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', -45)
      .attr('x', -height / 2)
      .attr('text-anchor', 'middle')
      .style('font-size', '12px')
      .style('fill', '#666')
      .text('Кількість артефактів');

    const xAxis = d3.axisBottom(xScale)
      .tickValues([0, 0.15, 0.3, 0.45, 0.6, 0.7, 0.8, 0.9, 0.96, 1.0].map(t => t))
      .tickFormat(tickVal => {
        const d = Math.round(invertTransformedDate(tickVal));
        if (d < 0) return `${Math.abs(d)} ${t.bce}`;
        if (d === 0) return '0';
        if (d === 2022) return t.noDate;
        return d.toString();
      });

    g.append('g')
      .attr('class', 'x-axis')
      .attr('transform', `translate(0,${height})`)
      .call(xAxis)
      .selectAll('text')
      .style('font-size', '11px')
      .attr('text-anchor', 'middle');

    const xAxisContext = d3.axisBottom(xScaleContext)
      .tickValues([0, 0.2, 0.4, 0.6, 0.8, 0.96, 1.0])
      .tickFormat(tickVal => {
        const d = Math.round(invertTransformedDate(tickVal));
        if (d < 0) return `${Math.abs(d)} ${t.bce}`;
        if (d === 0) return '0';
        if (d === 2022) return t.noDate;
        return d.toString();
      });

    contextG.append('g')
      .attr('class', 'x-axis-context')
      .attr('transform', `translate(0,${contextHeight})`)
      .call(xAxisContext)
      .selectAll('text')
      .style('font-size', '9px')
      .attr('text-anchor', 'middle');

    // Add centered title below context
    svg.append('text')
      .attr('x', margin.left + width / 2)
      .attr('y', margin.top + height + contextMarginTop + contextHeight + 60)
      .attr('text-anchor', 'middle')
      .style('font-size', '16px')
      .style('font-weight', 'bold')
      .style('fill', '#333')
      .text(t.historicalTime);

    // Horizontal legend below the title
    const legendGroup = svg.append('g')
      .attr('transform', `translate(${margin.left + width / 2}, ${margin.top + height + contextMarginTop + contextHeight + 80})`);

    const legendItems = [
      { key: 'Hermitage', label: t.hermitageFull, color: '#91CC75' },
      { key: 'SHM', label: t.shmFull, color: '#5470C6' }
    ];

    legendItems.forEach((item, i) => {
      const legendRow = legendGroup.append('g')
        .attr('transform', `translate(${i === 0 ? -250 : 20}, 0)`);
      
      legendRow.append('rect')
        .attr('width', 18)
        .attr('height', 18)
        .attr('fill', item.color)
        .attr('opacity', 0.7);
      
      legendRow.append('text')
        .attr('x', 24)
        .attr('y', 14)
        .style('font-size', '13px')
        .style('fill', '#333')
        .text(item.label);
    });

    const brushHandler = (event) => {
      if (!event.selection) return;
      
      // Skip if we're initializing the brush
      if (isInitializingBrush.current) {
        isInitializingBrush.current = false;
        return;
      }
      
      const [x0, x1] = event.selection;
      const newRange = [
        Math.round(xScaleContextDate.invert(x0)),
        Math.round(xScaleContextDate.invert(x1))
      ];
      
      // Update xScale to use new date range with transformation
      const t0 = transformDate(newRange[0]);
      const t1 = transformDate(newRange[1]);
      xScale.domain([t0, t1]);
      
      // Recreate xScaleDate wrapper with new domain
      const xScaleDateZoomed = (date) => xScale(transformDate(date));
      xScaleDateZoomed.domain = () => newRange;
      
      // Update axis with new domain
      const updatedXAxis = d3.axisBottom(xScale)
        .tickValues([t0, t0 + (t1-t0)*0.25, t0 + (t1-t0)*0.5, t0 + (t1-t0)*0.75, t1])
        .tickFormat(tickVal => {
          const d = Math.round(invertTransformedDate(tickVal));
          if (d < 0) return `${Math.abs(d)} ${t.bce}`;
          if (d === 0) return '0';
          if (d === 2022) return t.noDate;
          return d.toString();
        });
      
      g.select('.x-axis').call(updatedXAxis);
      g.selectAll('.x-axis text')
        .style('font-size', '11px')
        .attr('text-anchor', 'middle');
      
      mainLayers.attr('d', area);
      drawPeriodLabels(xScaleDateZoomed);
      setTimeRange(newRange);
    };

    const brush = d3.brushX()
      .extent([[0, 0], [width, contextHeight]])
      .on('end', brushHandler);

    const brushG = contextG.append('g')
      .attr('class', 'brush')
      .call(brush);

    // Set initial brush selection without triggering state update
    isInitializingBrush.current = true;
    const initialSelection = [
      xScaleContextDate(timeRange[0]),
      xScaleContextDate(timeRange[1])
    ];
    brushG.call(brush.move, initialSelection);

  }, [allData.stream, language]);

  // Close artifact panel on Escape
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && selectedArtifact) {
        setSelectedArtifact(null);
        if (window.location.hash.startsWith('#artifact-')) {
          history.pushState('', document.title, window.location.pathname + window.location.search);
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [selectedArtifact]);

  // Track time on page
useEffect(() => {
  const startTime = Date.now();
  const handleUnload = () => {
    const seconds = Math.round((Date.now() - startTime) / 1000);
    trackEvent('time_on_page', { seconds });
  };
  window.addEventListener('beforeunload', handleUnload);
  return () => window.removeEventListener('beforeunload', handleUnload);
}, []);

  if (hash === '#admin') {
    return <AdminPage onBack={() => { window.location.hash = ''; }} />;
  }
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '20px',
        color: '#666'
      }}>
        Завантаження даних...
      </div>
    );
  }

  return (
    <>
      <style>{`
        /* Desktop styles for language switcher */
        @media (min-width: 769px) {
          .language-switcher {
            position: absolute !important;
          }
        }
        
        /* Mobile-first responsive design */
        @media (max-width: 768px) {
          /* Header adjustments */
          .language-switcher {
            position: static !important;
            margin-top: 15px !important;
            justify-content: center !important;
            order: 3 !important;
          }
          
          .app-title {
            font-size: 1.5em !important;
            padding: 0 10px;
            margin-bottom: 5px !important;
          }
          
          .app-subtitle {
            font-size: 12px !important;
            padding: 0 10px;
            margin-bottom: 0 !important;
          }
          
          .period-range {
            display: block;
            margin-top: 4px;
            font-size: 11px !important;
          }
          
          .stats-row {
            flex-direction: column !important;
            gap: 8px !important;
            align-items: flex-start !important;
            padding: 15px !important;
          }
          
          .stats-row > div {
            margin-left: 0 !important;
            margin-right: 0 !important;
            width: 100%;
          }
          
          .stats-row button {
            width: 100%;
            margin: 0 !important;
          }
          
          .map-container {
            max-height: 250px !important;
            overflow: hidden;
          }
          
          .content-flex {
            flex-direction: column !important;
            gap: 15px !important;
            padding: 10px !important;
          }
          
          .content-flex > div {
            max-width: none !important;
          }
          
          .artifact-table-container {
            max-height: 350px !important;
            overflow-y: auto !important;
            -webkit-overflow-scrolling: touch;
          }
          
          .tabs-container {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }
          
          .tabs-container button {
            font-size: 11px !important;
            padding: 10px 8px !important;
            white-space: nowrap;
          }
          
          h1 {
            font-size: 1.8em !important;
          }
          
          /* Ensure map SVG fits container on mobile */
          .map-container svg {
            max-height: 250px;
          }
        }
        
        /* Ensure scrollable areas work on mobile */
        .artifact-table-container {
          position: relative;
        }
      `}</style>
      <div style={{ fontFamily: 'Verdana, sans-serif' }}>
      <div style={{
        background: '#f8f9fa',
        padding: '20px',
        textAlign: 'center',
        borderBottom: '2px solid #dee2e6',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}>
        <h1 className="app-title" style={{ margin: 0, fontSize: '2.5em', color: '#333', order: 1 }}>
          {t.appTitle}
        </h1>
        <p className="app-subtitle" style={{ margin: '10px 0 0', color: '#666', fontSize: '14px', order: 2 }}>
          {t.subtitle}
        </p>
        
        {/* Language Switcher */}
        <div className="language-switcher" style={{ 
          position: 'absolute', 
          top: '20px', 
          right: '20px', 
          display: 'flex', 
          gap: '5px',
          order: 3
        }}>
          <button
            onClick={() => {setLanguage('uk'); trackEvent('language_switch', { language: 'uk' })}}
            aria-label="Переключити на українську"
            aria-pressed={language === 'uk'}
            style={{
              padding: '8px 16px',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              background: language === 'uk' ? '#007bff' : 'white',
              color: language === 'uk' ? 'white' : '#333',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: language === 'uk' ? 'bold' : 'normal'
            }}
          >
            УКР
          </button>
          <button
            onClick={() => {setLanguage('en'); trackEvent('language_switch', { language: 'en' })}}
            aria-label="Switch to English"
            aria-pressed={language === 'en'}
            style={{
              padding: '8px 16px',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              background: language === 'en' ? '#007bff' : 'white',
              color: language === 'en' ? 'white' : '#333',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: language === 'en' ? 'bold' : 'normal'
            }}
          >
            ENG
          </button>
        </div>
      </div>

      <div style={{
        padding: '15px 20px',
        background: 'white',
        borderBottom: '1px solid #dee2e6'
      }}>
        <div 
          style={{ position: 'relative', maxWidth: '600px', margin: '0 auto' }}
          onMouseEnter={() => {
            if (searchResults.length > 0) {
              setShowDropdown(true);
            }
          }}
          onMouseLeave={() => {
            setShowDropdown(false);
          }}
        >
          <input
            type="text"
            id="artifact-search"
            name="search"
            placeholder={t.searchPlaceholder}
            value={searchQuery}
            onChange={handleSearch}
            onFocus={() => {
              if (searchResults.length > 0) {
                setShowDropdown(true);
              }
            }}
            style={{
              width: '100%',
              padding: '12px 15px',
              fontSize: '14px',
              border: '2px solid #007bff',
              borderRadius: '4px',
              outline: 'none'
            }}
          />
          {showDropdown && searchResults.length > 0 && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              background: 'white',
              border: '1px solid #dee2e6',
              borderTop: 'none',
              maxHeight: '300px',
              overflowY: 'auto',
              zIndex: 1000,
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
            }}>
              {searchResults.map((result, idx) => (
                <div
                  key={idx}
                  onClick={() => {
                    setSearchQuery(result.Назва_uk || result.title || result.Название || '');
                    setSearchResults([]);
                    setShowDropdown(false);
                    trackEvent('search_select', { query: result.Назва_uk || result.title || result.Название });
                  }}
                  style={{
                    padding: '10px 15px',
                    borderBottom: '1px solid #f0f0f0',
                    cursor: 'pointer',
                    fontSize: '13px'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                >
                  <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                    {result.Назва_uk || result.title || result.Название}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {result['Місце_знахідки_uk'] || result['Место находки']} • {result.dates}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {searchQuery && (
          <div style={{ textAlign: 'center', marginTop: '10px', fontSize: '13px', color: '#666' }}>
            Знайдено: ДІМ {filteredData.shm.length} | Ермітаж {filteredData.herm.length}
          </div>
        )}
      </div>

      <div className="stats-row" style={{
        padding: '20px',
        display: 'flex',
        gap: '10px',
        alignItems: 'center',
        borderBottom: '1px solid #dee2e6',
        background: '#f8f9fa',
        flexWrap: 'wrap'
      }}>
        <button
          //onClick={() => setSelectionMode(!selectionMode)}
          onClick={() => { setSelectionMode(!selectionMode); trackEvent('selection_mode', { active: !selectionMode }); }}
          style={{
            padding: '10px 20px',
            fontSize: '14px',
            cursor: 'pointer',
            background: selectionMode ? '#dc3545' : '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            fontWeight: 'bold'
          }}
        >
          {selectionMode ? t.cancel : t.selectZone}
        </button>
        
        <button
          onClick={() => zoomRef.current && d3.select(mapRef.current).select('svg').transition().call(zoomRef.current.transform, d3.zoomIdentity)}
          style={{
            padding: '10px 20px',
            fontSize: '14px',
            cursor: 'pointer',
            background: 'white',
            border: '1px solid #333',
            borderRadius: '4px'
          }}
        >
          {t.resetZoom}
        </button>
        
        <div style={{ marginLeft: '20px', color: '#666', fontSize: '14px' }}>
          <strong>{t.period}:</strong>{' '}
          <span className="period-range">
            {timeRange[0] < 0 ? `${Math.abs(timeRange[0])} ${t.bce}` : (timeRange[0] === 0 ? '0' : timeRange[0])}
            {' - '}
            {timeRange[1] === 2022 ? t.noDate : (timeRange[1] < 0 ? `${Math.abs(timeRange[1])} ${t.bce}` : timeRange[1])}
          </span>
        </div>
        <div style={{ marginLeft: 'auto', fontSize: '14px', color: '#666' }}>
          <strong>{t.total}:</strong> {t.shm} {filteredData.shm.length} | {t.hermitage} {filteredData.herm.length}
        </div>
      </div>

      <div className="content-flex" style={{ display: 'flex', padding: '20px', gap: '30px' }}>
          <div style={{ flex: '1' }}>
            <div ref={mapRef} className="map-container" style={{ border: '1px solid #333', background: 'white' }}></div>
            {selectionMode && (
              <div style={{
                marginTop: '10px',
                padding: '10px',
                background: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '4px',
                fontSize: '14px'
              }}>
                ℹ️ {t.dragToSelect}
              </div>
            )}
          </div>

          <div style={{ flex: '1', maxWidth: '550px' }}>
            <div className="tabs-container" style={{ display: 'flex', borderBottom: '2px solid #dee2e6' }}>
              {[t.homePage, t.shmFull, t.hermitageFull].map((tab, index) => (
                <button
                  key={index}
                  //onClick={() => setSelectedTab(index)}
                  onClick={() => { setSelectedTab(index); trackEvent('tab_click', { tab: index }); }}
                  style={{
                    padding: '12px 16px',
                    flex: 1,
                    border: 'none',
                    background: selectedTab === index ? '#007bff' : '#f8f9fa',
                    color: selectedTab === index ? 'white' : '#333',
                    cursor: 'pointer',
                    borderBottom: selectedTab === index ? '3px solid #0056b3' : 'none',
                    fontSize: '13px'
                  }}
                >
                  {tab}
                </button>
              ))}
            </div>

            <div className="artifact-table-container" style={{
              padding: '20px',
              maxHeight: '620px',
              overflowY: 'auto',
              background: 'white',
              fontSize: '14px',
              lineHeight: '1.7',
              border: '1px solid #dee2e6',
              borderTop: 'none'
            }}>
              {selectedTab === 0 && (
                <div>
                  {/* AI translation notice */}
                  <div style={{
                    background: '#fff8e1',
                    border: '1px solid #ffe082',
                    borderRadius: '4px',
                    padding: '10px 14px',
                    marginBottom: '16px',
                    fontSize: '13px',
                    color: '#555'
                  }}>
                    ⚠️ {t.welcomeText}
                  </div>

                  {/* About the Project */}
                  <h3 style={{ fontSize: '16px', color: '#333', marginTop: 0, marginBottom: '12px' }}>
                    {t.aboutProject}
                  </h3>
                  <p style={{ marginBottom: '12px' }}>
                    {t.aboutText}
                  </p>
                  <p style={{ marginBottom: '20px' }}>
                    {t.aboutViz}
                  </p>

                  {/* How to Use */}
                  <h3 style={{ fontSize: '16px', color: '#333', marginBottom: '12px', borderTop: '1px solid #dee2e6', paddingTop: '15px' }}>
                    {t.howToUse}
                  </h3>
                  <p style={{ marginBottom: '10px' }}>🔵 {t.howCircles}</p>
                  <p style={{ marginBottom: '10px' }}>📊 {t.howTimeline}</p>
                  <p style={{ marginBottom: '10px' }}>🔍 {t.howSearch}</p>
                  <p style={{ marginBottom: '10px' }}>🖼️ {t.howZone}</p>
                  <p style={{ marginBottom: '10px' }}>🎨 {t.howCollage}</p>
                  <p style={{ marginBottom: '20px' }}>📂 {t.howTabs}</p>

                  {/* How to Discover Stories */}
                  <h3 style={{ fontSize: '16px', color: '#333', marginBottom: '12px', borderTop: '1px solid #dee2e6', paddingTop: '15px' }}>
                    {t.discoverTitle}
                  </h3>
                  <p style={{ marginBottom: '15px', fontStyle: 'italic' }}>
                    {t.discoverIntro}
                  </p>
                  <p style={{ marginBottom: '10px' }}>
                    <strong>{t.discoverRegion}</strong> {t.discoverRegionText}
                  </p>
                  <p style={{ marginBottom: '10px' }}>
                    <strong>{t.discoverTime}</strong> {t.discoverTimeText}
                  </p>
                  <p style={{ marginBottom: '10px' }}>
                    <strong>{t.discoverPatterns}</strong> {t.discoverPatternsText}
                  </p>
                  <p style={{ marginBottom: '10px' }}>
                    <strong>{t.discoverMuseums}</strong> {t.discoverMuseumsText}
                  </p>
                  <p style={{ marginBottom: '20px' }}>
                    <strong>{t.discoverConnections}</strong> {t.discoverConnectionsText}
                  </p>

                  {/* Why This Matters */}
                  <h3 style={{ fontSize: '16px', color: '#333', marginBottom: '12px', borderTop: '1px solid #dee2e6', paddingTop: '15px' }}>
                    {t.whyMattersTitle}
                  </h3>
                  <p style={{ marginBottom: '20px' }}>
                    {t.whyMattersText}
                  </p>
                  
                  {/* Copyright and Collage notices */}
                  <div style={{ 
                    fontSize: '11px', 
                    marginTop: '10px', 
                    padding: '15px',
                    background: '#e7f3ff',
                    borderLeft: '3px solid #007bff',
                    color: '#333'
                  }}>
                    <p style={{ marginBottom: '10px' }}><strong>{t.copyrightNotice}</strong></p>
                    <p style={{ margin: 0 }}>
                      {t.copyrightText}
                    </p>
                  </div>
                </div>
              )}
              {selectedTab === 1 && (
                <div>
                

                  {selectedLocation && selectedLocation.type === 'SHM' && selectedLocation.data?.artifacts ? (
                    <div>
                      <p style={{ marginBottom: '10px', fontSize: '14px' }}>
                        <strong>{t.resultCount}</strong> {selectedLocation.data.artifacts.length}
                      </p>
                      {selectedLocation.data.coords && allData.labels?.labels_shm?.[selectedLocation.data.coords]?.[0] && (
                        <p style={{ marginBottom: '10px', fontSize: '13px', color: '#666' }}>
                          <strong>{language === 'uk' ? 'Місцезнаходження:' : 'Location:'}</strong> {allData.labels.labels_shm[selectedLocation.data.coords][0]}
                        </p>
                      )}
                      
                      <button
                        onClick={() => {
                          setSelectedArtifacts(selectedLocation.data.artifacts);
                        }}
                        style={{
                          padding: '8px 16px',
                          background: '#007bff',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          marginBottom: '15px',
                          fontSize: '13px'
                        }}
                      >
                        {t.viewCollage}
                      </button>

                      <div style={{ marginBottom: '15px' }}>
                        <label htmlFor="shm-page-select" style={{ display: 'none' }}>{t.page}</label>
                        <select 
                          id="shm-page-select"
                          name="shmPage"
                          value={currentPage}
                          onChange={(e) => setCurrentPage(Number(e.target.value))}
                          style={{
                            padding: '8px',
                            fontSize: '13px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            width: '100%'
                          }}
                        >
                          {Array.from({ length: Math.ceil(selectedLocation.data.artifacts.length / itemsPerPage) }, (_, i) => (
                            <option key={i + 1} value={i + 1}>
                              {t.page} #{i + 1} ({i * itemsPerPage + 1}-{Math.min((i + 1) * itemsPerPage, selectedLocation.data.artifacts.length)} {t.resources})
                            </option>
                          ))}
                        </select>
                      </div>

                      <div style={{ overflowX: 'auto', maxHeight: '450px', overflowY: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                          <thead style={{ position: 'sticky', top: 0, background: '#f8f9fa', zIndex: 1 }}>
                            <tr style={{ borderBottom: '2px solid #dee2e6' }}>
                              <th style={{ padding: '8px', textAlign: 'left' }}>{t.image}</th>
                              <th style={{ padding: '8px', textAlign: 'left' }}>{t.title}</th>
                              <th style={{ padding: '8px', textAlign: 'left' }}>{t.date}</th>
                              <th style={{ padding: '8px', textAlign: 'left' }}>{t.location}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedLocation.data.artifacts
                              .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                              .map((artifact, idx) => (
                              <tr key={idx} style={{ borderBottom: '1px solid #e0e0e0', cursor: 'pointer' }} onClick={() => { setSelectedArtifact(artifact); window.location.hash = `artifact-${artifact.id}`; }} title={language === 'uk' ? 'Натисніть для деталей' : 'Click for details'}>
                                <td style={{ padding: '8px' }}>
                                  {(artifact.img || artifact.img1) && (
                                    <div className="img-wrapper" style={{ width: '60px', height: '60px' }}>
                                      <img 
                                        src={artifact.img || artifact.img1} 
                                        alt={artifact.Назва_uk || artifact.title || artifact.Название}
                                        loading="lazy" decoding="async" style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                                        onLoad={(e) => {
                                          e.target.classList.add('loaded');
                                          e.target.parentElement.style.animation = 'none';
                                          e.target.parentElement.style.background = 'none';
                                        }}
                                        onError={(e) => {
                                          e.target.parentElement.style.display = 'none';
                                        }}
                                      />
                                    </div>
                                  )}
                                </td>
                                <td style={{ padding: '8px', fontSize: '11px' }}>
                                  {artifact.Назва_uk || artifact.title || artifact.Название || 'N/A'}
                                </td>
                                <td style={{ padding: '8px', fontSize: '11px' }}>
                                  {formatDate(artifact.dates) || 'N/A'}
                                </td>
                                <td style={{ padding: '8px', fontSize: '11px' }}>
                                  {artifact['Місце_знахідки_uk'] || artifact['Место находки'] || 'N/A'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                    </div>
                  ) : (
                    <p style={{ color: '#666', fontSize: '14px', marginTop: '15px' }}>
                      {t.clickCircle}
                    </p>
                  )}
                </div>
              )}
              {selectedTab === 2 && (
                <div>
                  

                  {selectedLocation && selectedLocation.type === 'Hermitage' && selectedLocation.data?.artifacts ? (
                    <div>
                      <p style={{ marginBottom: '15px', fontSize: '14px' }}>
                        <strong>{t.resultCount}</strong> {selectedLocation.data.artifacts.length}
                      </p>
                      {selectedLocation.data.coords && allData.labels?.labels_herm?.[selectedLocation.data.coords]?.[0] && (
                        <p style={{ marginBottom: '15px', fontSize: '13px', color: '#666' }}>
                          <strong>{language === 'uk' ? 'Місцезнаходження:' : 'Location:'}</strong> {allData.labels.labels_herm[selectedLocation.data.coords][0]}
                        </p>
                      )}

                      <button
                        onClick={() => {
                          setSelectedArtifacts(selectedLocation.data.artifacts);
                        }}
                        style={{
                          padding: '8px 16px',
                          background: '#28a745',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          marginBottom: '15px',
                          fontSize: '13px'
                        }}
                      >
                        {t.viewCollage}
                      </button>

                      <div style={{ marginBottom: '15px' }}>
                        <label htmlFor="hermitage-page-select" style={{ display: 'none' }}>{t.page}</label>
                        <select 
                          id="hermitage-page-select"
                          name="hermitagePage"
                          value={currentPage}
                          onChange={(e) => setCurrentPage(Number(e.target.value))}
                          style={{
                            padding: '8px',
                            fontSize: '13px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            width: '100%'
                          }}
                        >
                          {Array.from({ length: Math.ceil(selectedLocation.data.artifacts.length / itemsPerPage) }, (_, i) => (
                            <option key={i + 1} value={i + 1}>
                              {t.page} #{i + 1} ({i * itemsPerPage + 1}-{Math.min((i + 1) * itemsPerPage, selectedLocation.data.artifacts.length)} {t.resources})
                            </option>
                          ))}
                        </select>
                      </div>

                      <div style={{ overflowX: 'auto', maxHeight: '450px', overflowY: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                          <thead style={{ position: 'sticky', top: 0, background: '#f8f9fa', zIndex: 1 }}>
                            <tr style={{ borderBottom: '2px solid #dee2e6' }}>
                              <th style={{ padding: '8px', textAlign: 'left' }}>{t.image}</th>
                              <th style={{ padding: '8px', textAlign: 'left' }}>{t.title}</th>
                              <th style={{ padding: '8px', textAlign: 'left' }}>{t.date}</th>
                              <th style={{ padding: '8px', textAlign: 'left' }}>{t.location}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedLocation.data.artifacts
                              .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                              .map((artifact, idx) => (
                              <tr key={idx} style={{ borderBottom: '1px solid #e0e0e0', cursor: 'pointer' }} onClick={() => { setSelectedArtifact(artifact); window.location.hash = `artifact-${artifact.id}`; }} title={language === 'uk' ? 'Натисніть для деталей' : 'Click for details'}>
                                <td style={{ padding: '8px' }}>
                                  {(artifact.img || artifact.img1) && (
                                    <div className="img-wrapper" style={{ width: '60px', height: '60px' }}>
                                      <img
                                        src={artifact.img || artifact.img1}
                                        alt={artifact.Назва_uk || artifact.title || artifact.Название}
                                        loading="lazy" decoding="async" style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                                        onLoad={(e) => {
                                          e.target.classList.add('loaded');
                                          e.target.parentElement.style.animation = 'none';
                                          e.target.parentElement.style.background = 'none';
                                        }}
                                        onError={(e) => {
                                          e.target.parentElement.style.display = 'none';
                                        }}
                                      />
                                    </div>
                                  )}
                                </td>
                                <td style={{ padding: '8px', fontSize: '11px' }}>
                                  {artifact.Назва_uk || artifact.title || artifact.Название || 'N/A'}
                                </td>
                                <td style={{ padding: '8px', fontSize: '11px' }}>
                                  {formatDate(artifact.dates) || 'N/A'}
                                </td>
                                <td style={{ padding: '8px', fontSize: '11px' }}>
                                  {artifact['Місце_знахідки_uk'] || artifact['Место находки'] || 'N/A'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                    </div>
                  ) : (
                    <p style={{ color: '#666', fontSize: '14px', marginTop: '15px' }}>
                      {t.clickCircle}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

      <div style={{ padding: '20px', background: '#f8f9fa' }}>
        <h3 style={{ 
          marginBottom: '15px', 
          fontSize: '18px',
          color: '#333',
          borderBottom: '2px solid #007bff',
          paddingBottom: '10px'
        }}>
          {t.timelineInstruction}
        </h3>
        <div ref={streamgraphRef} style={{ background: 'white', padding: '10px', border: '1px solid #dee2e6' }}></div>
      </div>

      {/* ── Artifact Detail Panel ───────────────────────────────────────── */}
      {selectedArtifact && (() => {
        const a = selectedArtifact;
        const name = language === 'uk'
          ? (a.Назва_uk || a.title || a.Название || t.noTitle)
          : (a.title || a.Назва_uk || a.Название || t.noTitle);
        const place = language === 'uk'
          ? (a['Місце_знахідки_uk'] || a['Место находки'] || '')
          : (a['Место находки'] || a['Місце_знахідки_uk'] || '');
        const dateStr = formatDate(a.dates) || t.dateUnknown;
        const museum = a.source === 'SHM' ? t.shmFull : t.hermitageFull;

        // Direct link — used for copy-to-clipboard (opens panel directly)
        const directUrl = `${window.location.origin}${window.location.pathname}#artifact-${a.id}`;

        // Social share URL — goes through Cloudflare Worker so Facebook/Twitter/Telegram
        // crawlers get proper OG meta tags with the artifact image.
        // Replace with your actual worker URL after deploying cloudflare-worker.js
        const WORKER_BASE = 'https://divine-heart-25cb.obuchel.workers.dev'; // ← update this after deploy
        const socialUrl = `${WORKER_BASE}/artifact/${a.id}`;

        // Mini timeline helpers
        // Timeline uses the actual artifact date range, not geological time
        // Data spans ~3000 BCE to 2022 CE — keeps the marker meaningful
        const TMIN = -3000, TMAX = 2022;
        const pct = (year) => Math.max(0, Math.min(100, ((year - TMIN) / (TMAX - TMIN)) * 100));
        const markerPct = a.dates ? pct(+a.dates) : null;

        const closePanel = () => {
          setSelectedArtifact(null);
          // Restore hash without artifact
          const h = window.location.hash;
          if (h.startsWith('#artifact-')) {
            history.pushState('', document.title, window.location.pathname + window.location.search);
          }
        };

        const copyLink = () => {
          navigator.clipboard.writeText(directUrl);
        };

        return (
          <>
            {/* Backdrop */}
            <div onClick={closePanel} style={{
              position: 'fixed', inset: 0,
              background: 'rgba(0,0,0,0.5)',
              zIndex: 1100,
              backdropFilter: 'blur(2px)',
            }} />
            {/* Slide-in panel */}
            <div
              role="dialog"
              aria-modal="true"
              aria-label={name}
              style={{
                position: 'fixed',
                top: 0, right: 0, bottom: 0,
                width: 'min(500px, 100vw)',
                background: '#fff',
                zIndex: 1101,
                overflowY: 'auto',
                boxShadow: '-6px 0 32px rgba(0,0,0,0.25)',
                animation: 'artifactSlideIn 0.25s cubic-bezier(0.4,0,0.2,1)',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {/* Header */}
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '14px 18px',
                background: '#1A3C6E',
                position: 'sticky', top: 0, zIndex: 10, gap: '8px',
              }}>
                <span style={{
                  fontSize: '11px', fontWeight: 700, color: '#C8A94A',
                  background: 'rgba(200,169,74,0.15)',
                  padding: '3px 10px', borderRadius: '3px',
                  border: '1px solid rgba(200,169,74,0.4)',
                  textTransform: 'uppercase', letterSpacing: '1px', flexShrink: 0,
                }}>
                  {a.source}
                </span>
                <div style={{ display: 'flex', gap: '6px', marginLeft: 'auto' }}>
                  <button onClick={copyLink} title={language === 'uk' ? 'Скопіювати посилання' : 'Copy link'}
                    style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)', color: 'white', borderRadius: '5px', padding: '5px 10px', cursor: 'pointer', fontSize: '14px' }}>
                    🔗
                  </button>
                  <button onClick={closePanel} aria-label={language === 'uk' ? 'Закрити' : 'Close'}
                    style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)', color: 'white', borderRadius: '5px', padding: '5px 12px', cursor: 'pointer', fontSize: '16px', fontWeight: 300 }}>
                    ✕
                  </button>
                </div>
              </div>

              {/* Body */}
              <div style={{ padding: '20px', flex: 1 }}>

                {/* Name */}
                <h2 style={{ fontSize: '18px', fontWeight: 700, color: '#1A3C6E', marginBottom: '4px', lineHeight: 1.3 }}>
                  {name}
                </h2>
                {a.Назва_uk && a.title && language === 'en' && (
                  <p style={{ fontSize: '13px', color: '#888', marginBottom: '14px', fontStyle: 'italic' }}>{a.Назва_uk}</p>
                )}

                {/* Image */}
                {(a.img || a.img1) ? (
                  <div style={{ marginBottom: '18px', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e5e5e5', background: '#f5f5f5' }}>
                    <img src={a.img || a.img1} alt={name} loading="lazy"
                      style={{ width: '100%', maxHeight: '280px', objectFit: 'contain', display: 'block' }}
                      onError={e => { e.target.parentNode.style.display = 'none'; }} />
                  </div>
                ) : (
                  <div style={{
                    marginBottom: '18px', height: '100px',
                    background: 'linear-gradient(135deg, rgba(26,60,110,0.08), rgba(200,169,74,0.08))',
                    borderRadius: '8px', border: '1px dashed rgba(200,169,74,0.5)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#aaa', fontSize: '13px',
                  }}>
                    {language === 'uk' ? 'Зображення відсутнє' : 'No image available'}
                  </div>
                )}

                {/* Metadata grid */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '18px' }}>
                  {[
                    [language === 'uk' ? 'Дата' : 'Date', dateStr],
                    [language === 'uk' ? 'Музей' : 'Museum', museum],
                    [language === 'uk' ? 'Місце знахідки' : 'Find Location', place],
                    [language === 'uk' ? 'Джерело' : 'Source', a.source],
                  ].filter(([, v]) => v).map(([label, value]) => (
                    <div key={label} style={{ background: '#f4f6fb', borderRadius: '6px', padding: '10px 12px' }}>
                      <div style={{ fontSize: '10px', color: '#888', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '2px' }}>{label}</div>
                      <div style={{ fontSize: '13px', fontWeight: 500, color: '#222' }}>{value}</div>
                    </div>
                  ))}
                </div>

                {/* Mini Timeline */}
                {markerPct !== null && (
                  <div style={{ marginBottom: '18px' }}>
                    <div style={{ fontSize: '11px', fontWeight: 600, color: '#555', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>
                      {language === 'uk' ? 'Хронологія' : 'Timeline'}
                    </div>
                    <div style={{ position: 'relative', height: '44px', padding: '0 2px' }}>
                      {/* Track */}
                      <div style={{ position: 'absolute', top: '12px', left: 2, right: 2, height: '8px', background: 'linear-gradient(to right, #3a7bd5 0%, #C8A94A 55%, #8B1A1A 100%)', borderRadius: '4px', opacity: 0.3 }} />
                      {/* Era ticks — 3 only, anchored to avoid edge overflow */}
                      {[
                        { year: -3000, label: language === 'uk' ? '3000 до н.е.' : '3000 BCE', anchor: 'start' },
                        { year: 0,     label: '0',                                              anchor: 'middle' },
                        { year: 2022,  label: language === 'uk' ? 'сьогодні' : 'today',         anchor: 'end' },
                      ].map(({ year, label, anchor }) => {
                        const p = pct(year);
                        const left = anchor === 'start' ? '0%' : anchor === 'end' ? '100%' : `${p}%`;
                        const transform = anchor === 'start' ? 'none' : anchor === 'end' ? 'translateX(-100%)' : 'translateX(-50%)';
                        return (
                          <div key={year} style={{ position: 'absolute', top: '10px', left, transform }}>
                            <div style={{ width: '1px', height: '12px', background: '#bbb', margin: anchor === 'start' ? '0' : anchor === 'end' ? '0 0 0 auto' : '0 auto' }} />
                            <div style={{ fontSize: '9px', color: '#999', whiteSpace: 'nowrap', marginTop: '2px' }}>{label}</div>
                          </div>
                        );
                      })}
                      {/* Marker with period label above it */}
                      <div style={{ position: 'absolute', top: '6px', left: `${markerPct}%`, transform: 'translateX(-50%)', zIndex: 2 }}>
                        <div style={{ width: '16px', height: '16px', background: '#C8A94A', border: '2.5px solid #1A3C6E', borderRadius: '50%', boxShadow: '0 1px 4px rgba(0,0,0,0.3)' }} />
                      </div>
                    </div>
                    {/* Date centered below bar */}
                    <div style={{ textAlign: 'center', fontSize: '13px', fontWeight: 500, color: '#1A3C6E', marginTop: '4px' }}>{dateStr}</div>
                  </div>
                )}

                {/* Keywords */}
                {a['Ключевые слова'] && (
                  <div style={{ marginBottom: '18px' }}>
                    <div style={{ fontSize: '11px', fontWeight: 600, color: '#555', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>
                      {language === 'uk' ? 'Ключові слова' : 'Keywords'}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {a['Ключевые слова'].split(',').slice(0, 8).map((kw, i) => (
                        <span key={i} style={{ background: '#EEF2F8', color: '#1A3C6E', fontSize: '11px', padding: '3px 8px', borderRadius: '3px', border: '1px solid #d0d7e3' }}>
                          {kw.trim()}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Share */}
                <div style={{ borderTop: '1px solid #eee', paddingTop: '14px', display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <span style={{ fontSize: '12px', color: '#888' }}>{language === 'uk' ? 'Поділитися:' : 'Share:'}</span>
                  {[
                    { label: 'Twitter/X', bg: '#000', url: `https://twitter.com/intent/tweet?url=${encodeURIComponent(socialUrl)}&text=${encodeURIComponent(name)}` },
                    { label: 'Facebook',  bg: '#1877F2', url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(socialUrl)}` },
                    { label: 'Telegram',  bg: '#0088cc', url: `https://t.me/share/url?url=${encodeURIComponent(socialUrl)}&text=${encodeURIComponent(name)}` },
                  ].map(({ label, bg, url }) => (
                    <a key={label} href={url} target="_blank" rel="noopener noreferrer"
                      onClick={e => { e.preventDefault(); window.open(url, '_blank', 'width=600,height=400'); }}
                      style={{ padding: '5px 12px', borderRadius: '4px', background: bg, color: 'white', fontSize: '11px', textDecoration: 'none', fontWeight: 500 }}>
                      {label}
                    </a>
                  ))}
                </div>
              </div>
            </div>
            <style>{`@keyframes artifactSlideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>
          </>
        );
      })()}

      {selectedArtifacts.length > 0 && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '40px'
          }}
          onClick={() => setSelectedArtifacts([])}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '8px',
              maxWidth: '90vw',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
              padding: '30px'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginTop: 0, marginBottom: '20px' }}>
              {t.viewCollage} ({selectedArtifacts.filter(a => a.img || a.img1).length} {t.resources.toLowerCase()})
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
              gap: '12px'
            }}>
              {selectedArtifacts.filter(a => a.img || a.img1).map((artifact, idx) => (
                <div key={idx} style={{ border: '1px solid #ddd', borderRadius: '4px', overflow: 'hidden' }}>
                  <div className="img-wrapper" style={{ width: '100%', height: '180px' }}>
                    <img 
                      src={artifact.img || artifact.img1} 
                      alt={artifact.Назва_uk || artifact.title || artifact.Название} 
                      loading="lazy"
                      decoding="async"
                      style={{ width: '100%', height: '180px', objectFit: 'contain', background: '#f5f5f5' }}
                      onLoad={(e) => {
                        e.target.classList.add('loaded');
                        e.target.parentElement.style.animation = 'none';
                        e.target.parentElement.style.background = 'none';
                      }}
                      onError={(e) => {
                        e.target.parentElement.parentElement.style.display = 'none';
                      }}
                    />
                  </div>
                  <div style={{ padding: '8px', fontSize: '12px' }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                      {(artifact.Назва_uk || artifact.title || artifact.Название || t.noTitle).substring(0, 50)}
                    </div>
                    <div style={{ color: '#666' }}>
                      {formatDate(artifact.dates) || t.dateUnknown}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <button
              onClick={() => setSelectedArtifacts([])}
              style={{
                marginTop: '20px',
                padding: '12px 30px',
                background: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
                display: 'block',
                margin: '20px auto 0'
              }}
            >
              {t.closeCollage}
            </button>
          </div>
        </div>
      )}

      <div style={{
        marginTop: '40px',
        padding: '30px 20px',
        background: '#f8f9fa',
        borderTop: '2px solid #dee2e6'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: '14px',
          flexWrap: 'wrap',
          gap: '15px'
        }}>
          <div>
            <a href="https://dataforlibs.github.io" aria-label="Dataforlibs — головна сторінка" style={{ marginRight: '20px', fontWeight: 'bold', color: '#007bff' }}>
              Dataforlibs
            </a>
            <a href="https://texty.org.ua/d/2023/stolen_heritage/" target="_blank" rel="noopener noreferrer" aria-label="Texty.org — публікація про Украдені скарби (відкриється в новому вікні)" style={{ color: '#007bff' }}>
              Texty.org - публікація про Украдені скарби
            </a>
          </div>
          <div style={{ color: '#666' }}>
            © Dataforlibs 2026
          </div>
        </div>
      </div>
    </div>
    </>
  );
}

export default App;
